/**
 * Expands the UI expression graph for backend consumption and for s-expression logging.
 * - Resolves constant nodes to literal nodes.
 * - Expands seriesAware parameters to multiple ohlc_series (or indicator_series) nodes.
 * - Expands seriesAwareWindow parameters to lookbackBars × requiredFields ohlc_series nodes.
 */
import { functionRegistry } from './functionRegistry';
import type { ExpressionConstant, ExpressionGraph } from '../state/types';

export function expandExpressionGraph(
  graph: ExpressionGraph,
  constants: ExpressionConstant[]
): ExpressionGraph {
  const constantsById = new Map(constants.map((c) => [c.id, c.value]));
  const newExpressions = { ...graph.expressions };

  // Resolve constant nodes to literal nodes (backend only accepts literal)
  Object.keys(newExpressions).forEach((nodeId) => {
    const node = newExpressions[nodeId];
    if (node?.type === 'constant') {
      const value = constantsById.get(node.constantId) ?? 0;
      newExpressions[nodeId] = {
        id: node.id,
        type: 'literal',
        value,
      };
    }
  });

  // Process all function nodes
  Object.values(newExpressions).forEach((node) => {
    if (node.type !== 'function') return;

    const def = functionRegistry.get(node.functionId);
    if (!def) return;

    const newOperandIds = node.operandIds.map((childId, idx) => {
      if (!childId) return childId;

      const child = newExpressions[childId];
      const param = def.parameters[idx];

      // Expand seriesAware → multiple ohlc_series
      if (param?.type === 'seriesAware' && child?.type === 'seriesAware') {
        delete newExpressions[childId];
        return child.requiredFields.map((field) => {
          const expandedId = `exp-${childId}-${field}`;
          newExpressions[expandedId] = {
            id: expandedId,
            type: 'ohlc_series',
            seriesId: child.seriesId,
            field: field as 'open' | 'high' | 'low' | 'close' | 'volume' | 'date',
            offset: child.offset ?? 0,
          };
          return expandedId;
        });
      }

      // Expand seriesAwareWindow → lookbackBars × requiredFields ohlc_series (order: baseOffset+0 .. baseOffset+(N-1))
      if (param?.type === 'seriesAwareWindow' && child?.type === 'seriesAware') {
        delete newExpressions[childId];
        const { lookbackBars, requiredFields } = param;
        const baseOffset = child.offset ?? 0;
        const ids: string[] = [];
        for (let i = 0; i < lookbackBars; i++) {
          const barOffset = baseOffset + i;
          for (const field of requiredFields) {
            const expandedId = `exp-${childId}-${barOffset}-${field}`;
            newExpressions[expandedId] = {
              id: expandedId,
              type: 'ohlc_series',
              seriesId: child.seriesId,
              field: field as 'open' | 'high' | 'low' | 'close' | 'volume' | 'date',
              offset: barOffset,
            };
            ids.push(expandedId);
          }
        }
        return ids;
      }
      return childId;
    }).flat();

    // Backend expects operandIds: string[] (no nulls)
    const filteredIds = newOperandIds.filter((id): id is string => id != null);
    newExpressions[node.id] = { ...node, operandIds: filteredIds };
  });

  return { ...graph, expressions: newExpressions };
}
